/**
 * Appointment Facade
 *
 * Simplified, stable API for presentation layer to interact with appointment operations.
 * Abstracts away orchestration complexity.
 * @agentic connections
 */

import type { MedplumClient } from "@medplum/core";
import type {
  Appointment,
  Patient,
  PractitionerRole,
} from "@medplum/fhirtypes";
import { upsertAppointment } from "@benchmark/core/actions";
import type { AppointmentFormData } from "@benchmark/core/types";

/**
 * Facade for appointment operations. Provides stable API for create/update.
 * @agentic connections
 */
export class AppointmentFacade {
  constructor(private medplum: MedplumClient) {}

  /**
   * Create or update an appointment.
   * @agentic connections
   */
  async upsert(
    formData: AppointmentFormData,
    patient: Patient,
    referrer: PractitionerRole,
    existingAppointment?: Appointment,
  ): Promise<Appointment> {
    return await upsertAppointment(this.medplum, {
      formData,
      patient,
      referrer,
      existingAppointment,
    });
  }
}

/**
 * Creates an appointment facade instance bound to a MedplumClient.
 * @agentic connections
 */
export function createAppointmentFacade(
  medplum: MedplumClient,
): AppointmentFacade {
  return new AppointmentFacade(medplum);
}
